import type { ReactNode } from "react";
import clsx from "clsx";
import {
  ChevronsLeft,
  Crosshair,
  Filter,
  Layers,
  Map as MapIcon,
  MapPin,
  Plane,
  RotateCcw,
  Satellite,
  Settings2,
} from "lucide-react";
import { Spinner, Switch } from "../ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { MAP_COLORS, PROGRESS_COLORS, BOUNDARY_KINDS } from "../MapPreview";
import {
  NO_FILTER,
  blocksFor,
  describeFilter,
  isFiltered,
  reconcile,
  wardsFor,
  type Hierarchy,
  type HierarchyFilter,
} from "@/lib/hierarchyFilter";
import type { Basemap, BoundaryKind, MapViewState } from "@/lib/useMapView";
import type { EditableLayersState } from "@/lib/useEditableLayers";
import { editableLayerColor } from "@/lib/layerColors";
import { formatNumber } from "@/lib/format";
import { useLanguage } from "@/i18n";

const ALL = "all";
const natural = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const byName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => natural.compare(a.name, b.name));
const toValue = (id: number | null) => (id === null ? ALL : String(id));
const fromValue = (v: string) => (v === ALL ? null : Number(v));
const DROP_DOWN = { side: "bottom", avoidCollisions: false, position: "popper" } as const;

export type LayerCounts = {
  zone: number;
  ward: number;
  block: number;
  imageryReady: number;
  imageryTotal: number;
  features: number;
  numbered: number;
  pending: number;
  inProgress: number;
  submitted: number;
};

/**
 * The project dashboard's left panel: location filters on one tab, every map
 * layer and view option on the other. Replaces the switches that used to float
 * over the map and the filter bar under it, so all of them live in one place.
 */
export default function FiltersLayersPanel({
  onCollapse,
  hierarchy,
  filter,
  onFilterChange,
  shown,
  total,
  selectedOnly,
  onSelectedOnlyChange,
  mapView,
  counts,
  editable,
  onZoomToLayer,
  qa,
  onManageUploads,
}: {
  onCollapse: () => void;
  hierarchy: Hierarchy;
  filter: HierarchyFilter;
  onFilterChange: (next: HierarchyFilter) => void;
  shown: number;
  total: number;
  selectedOnly: boolean;
  onSelectedOnlyChange: (on: boolean) => void;
  mapView: MapViewState;
  counts: LayerCounts;
  editable: EditableLayersState;
  onZoomToLayer: (bbox: [number, number, number, number]) => void;
  /** The overlap & sliver check; omitted for accounts that cannot run it. */
  qa?: { enabled: boolean; onToggle: () => void; loading: boolean; overlaps?: number; slivers?: number };
  onManageUploads?: () => void;
}) {
  const { t, lang } = useLanguage();
  const w = t.workspace;
  const { view } = mapView;
  const n = (v: number) => formatNumber(v, lang);

  return (
    <aside className="flex h-full w-full flex-col bg-surface">
      <div className="flex flex-none items-center justify-between border-b border-line px-4 py-3">
        <h2 className="flex items-center gap-2 font-display text-[15px] font-bold uppercase tracking-[0.08em] text-ink"><span className="h-3.5 w-[3px] rounded-full bg-signal" aria-hidden="true" />{w.filtersAndLayers}</h2>
        <button
          type="button"
          onClick={onCollapse}
          title={w.collapsePanel}
          aria-label={w.collapsePanel}
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
      </div>

      <Tabs defaultValue="filter" className="flex min-h-0 flex-1 flex-col">
        <div className="flex-none px-4 pt-3">
          <TabsList className="grid h-10 w-full grid-cols-2">
            <TabsTrigger value="filter" className="gap-1.5 text-xs data-[state=active]:bg-accent-solid data-[state=active]:text-white">
              <Filter className="h-3.5 w-3.5" /> {w.filterTab}
            </TabsTrigger>
            <TabsTrigger value="layer" className="gap-1.5 text-xs data-[state=active]:bg-accent-solid data-[state=active]:text-white">
              <Layers className="h-3.5 w-3.5" /> {w.layerTab}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ── Filter ───────────────────────────────────────────────────── */}
        <TabsContent value="filter" className="thin-scroll mt-0 min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-4">
          <LocationFilter hierarchy={hierarchy} filter={filter} onChange={onFilterChange} />

          <div className="mt-4 rounded-md border border-line bg-surface-sunken/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink">{w.selectedAreaOnly}</span>
              <Switch
                checked={selectedOnly}
                onChange={onSelectedOnlyChange}
                label={w.selectedAreaOnly}
                disabled={!isFiltered(filter)}
              />
            </div>
            <p className="mt-1 text-2xs leading-snug text-ink-muted">{w.selectedAreaOnlyHint}</p>
          </div>

          <p className="mt-3 text-2xs text-ink-muted">
            {isFiltered(filter) ? (
              <>
                {t.hierarchyFilterBar.shownOfTotal(n(shown), n(total))}{" "}
                <span className="font-medium text-ink-secondary">{describeFilter(hierarchy, filter)}</span>
              </>
            ) : (
              w.allOfProject(n(total))
            )}
          </p>

          <button
            type="button"
            onClick={() => onFilterChange(reconcile(hierarchy, NO_FILTER))}
            disabled={!isFiltered(filter)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-line bg-surface py-2.5 text-xs font-semibold text-ink shadow-sm transition hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> {w.resetFilters}
          </button>
        </TabsContent>

        {/* ── Layer ────────────────────────────────────────────────────── */}
        <TabsContent value="layer" className="thin-scroll mt-0 min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-4 pt-4">
          <Section icon={<MapIcon className="h-3.5 w-3.5" />} title={w.baseMap}>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ["street", w.street, <MapIcon key="s" className="h-4 w-4" />, "from-[#E8EEF3] to-[#CFDCE5]"],
                  ["satellite", w.satellite, <Satellite key="t" className="h-4 w-4" />, "from-[#3B5A3A] to-[#1F2F27]"],
                ] as [Exclude<Basemap, "none">, string, ReactNode, string][]
              ).map(([value, label, icon, gradient]) => {
                const on = view.basemap === value && !view.visible.imagery;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => mapView.chooseBase(value)}
                    aria-pressed={on}
                    className={clsx(
                      "flex h-16 flex-col items-center justify-center gap-1 rounded-md border-2 bg-gradient-to-br text-2xs font-bold uppercase tracking-wide transition",
                      gradient,
                      value === "satellite" ? "text-white" : "text-ink-secondary",
                      on ? "border-accent-solid shadow-ring" : "border-transparent opacity-80 hover:opacity-100"
                    )}
                  >
                    {icon}
                    {label}
                  </button>
                );
              })}
              {/* Drone imagery: the third choice, shown alone on a plain
                  background. Picking Street or Satellite turns it off. */}
              <button
                type="button"
                onClick={() => mapView.chooseBase("drone")}
                aria-pressed={view.visible.imagery}
                title={`${t.projectWorkspacePage.droneImagery} ${n(counts.imageryReady)}/${n(counts.imageryTotal)}`}
                className={clsx(
                  "relative flex h-16 flex-col items-center justify-center gap-1 rounded-md border-2 bg-gradient-to-br from-[#7A6A4F] to-[#3F3526] text-2xs font-bold uppercase tracking-wide text-white transition",
                  view.visible.imagery ? "border-accent-solid shadow-ring" : "border-transparent opacity-60 hover:opacity-100"
                )}
              >
                <Plane className="h-4 w-4" />
                {w.drone}
                <span className="absolute right-1 top-1 rounded bg-black/30 px-1 text-[9px] font-semibold normal-case tracking-normal">
                  {n(counts.imageryReady)}/{n(counts.imageryTotal)}
                </span>
              </button>
            </div>
          </Section>

          <Section
            icon={<Layers className="h-3.5 w-3.5" />}
            title={w.boundaries}
            action={
              <button
                type="button"
                onClick={mapView.toggleAllBoundaries}
                className="text-2xs font-semibold text-accent-solid hover:underline"
              >
                {BOUNDARY_KINDS.some((k) => view.visible[k]) ? w.hideAll : w.showAll}
              </button>
            }
          >
            {BOUNDARY_KINDS.map((k: BoundaryKind) => (
              <LayerOption
                key={k}
                color={MAP_COLORS[k]}
                label={{ zone: t.mapPreview.boundaryZones, ward: t.mapPreview.boundaryWards, block: t.mapPreview.boundaryBlocks }[k]}
                count={n(counts[k])}
                on={view.visible[k]}
                onToggle={() => mapView.toggleLayer(k)}
                extra={
                  <button
                    type="button"
                    onClick={() => mapView.toggleBoundaryLabels(k)}
                    aria-pressed={view.boundaryLabels[k]}
                    title={`${t.mapPreview.boundaryLabels}: ${k}`}
                    className={clsx(
                      "rounded px-1.5 py-0.5 text-2xs font-semibold transition",
                      view.boundaryLabels[k] ? "bg-ink text-ink-inverse" : "text-ink-muted shadow-ring hover:text-ink"
                    )}
                  >
                    {t.mapPreview.boundaryLabels}
                  </button>
                }
              />
            ))}
          </Section>

          <Section icon={<MapPin className="h-3.5 w-3.5" />} title={w.surveyData}>
            <LayerOption
              color={MAP_COLORS.savedPolygon}
              label={t.mapPreview.fieldData}
              count={n(counts.features)}
              on={view.visible.features}
              onToggle={() => mapView.toggleLayer("features")}
            />
            <LayerOption
              color={MAP_COLORS.savedPolygon}
              label={t.mapPreview.fieldDataOutlineOnly}
              on={view.outlineOnly.features}
              onToggle={() => mapView.toggleOutlineOnly("features")}
              hint={t.mapPreview.outlineOnlyHint}
            />
            <LayerOption
              color="#1A1F2E"
              label={t.mapPreview.propertyNumbers}
              count={n(counts.numbered)}
              on={view.visible.labels}
              onToggle={() => mapView.toggleLayer("labels")}
              hint={w.numbersHint}
            />
          </Section>

          <Section
            icon={<Layers className="h-3.5 w-3.5" />}
            title={t.projectWorkspacePage.editableLayers}
            action={
              onManageUploads && (
                <button
                  type="button"
                  onClick={onManageUploads}
                  className="flex items-center gap-1 text-2xs font-semibold text-accent-solid hover:underline"
                >
                  <Settings2 className="h-3 w-3" /> {w.manage}
                </button>
              )
            }
          >
            {editable.layers === null ? (
              <p className="flex items-center gap-2 text-2xs text-ink-muted">
                <Spinner className="h-3 w-3" /> {t.editableLayersPanel.loadingUploads}
              </p>
            ) : editable.layers.length === 0 ? (
              <p className="text-2xs text-ink-muted">{w.noEditableLayers}</p>
            ) : (
              <>
                {editable.layers.map((layer) => (
                  <LayerOption
                    key={layer.id}
                    color={editableLayerColor(layer.id)}
                    label={layer.name}
                    count={editable.loadingIds.has(layer.id) ? "…" : n(layer.feature_count)}
                    on={!!editable.visible[layer.id]}
                    onToggle={() => editable.toggleVisible(layer.id)}
                    extra={
                      layer.bbox && (
                        <button
                          type="button"
                          onClick={() => onZoomToLayer(layer.bbox!)}
                          title={t.editableLayersPanel.zoomToLayer}
                          aria-label={t.editableLayersPanel.zoomToLayer}
                          className="flex h-5 w-5 items-center justify-center rounded text-ink-muted hover:bg-surface-sunken hover:text-ink"
                        >
                          <Crosshair className="h-3 w-3" />
                        </button>
                      )
                    }
                  />
                ))}
                <LayerOption
                  color="#1A1F2E"
                  label={t.mapPreview.propertyNumbersOnLayers}
                  on={view.editableLabels}
                  onToggle={mapView.toggleEditableLabels}
                />
                <LayerOption
                  color="#FFFFFF"
                  label={t.mapPreview.plotNumbersOnLayers}
                  on={view.editablePlotLabels}
                  onToggle={mapView.toggleEditablePlotLabels}
                />
                <LayerOption
                  color="#9CA3AF"
                  label={t.mapPreview.layersOutlineOnly}
                  on={view.outlineOnly.editable}
                  onToggle={() => mapView.toggleOutlineOnly("editable")}
                  hint={t.mapPreview.outlineOnlyHint}
                />
              </>
            )}
          </Section>

          <Section icon={<Filter className="h-3.5 w-3.5" />} title={w.analysis}>
            <LayerOption
              color={PROGRESS_COLORS.in_progress!}
              label={t.mapPreview.highlightProgress}
              on={view.highlightProgress}
              onToggle={mapView.toggleHighlightProgress}
            >
              {view.highlightProgress && (
                <Key
                  rows={[
                    [PROGRESS_COLORS.in_progress!, t.mapPreview.inProgress, n(counts.inProgress)],
                    [PROGRESS_COLORS.submitted!, t.mapPreview.submitted, n(counts.submitted)],
                    [MAP_COLORS.block, t.mapPreview.notStarted, n(counts.pending)],
                  ]}
                />
              )}
            </LayerOption>
            {qa && (
              <LayerOption
                color={MAP_COLORS.qaOverlap}
                label={t.mapPreview.qaCheck}
                on={qa.enabled}
                onToggle={qa.onToggle}
                count={qa.enabled && qa.loading ? "…" : undefined}
              >
                {qa.enabled && qa.overlaps !== undefined && (
                  <Key
                    rows={[
                      [MAP_COLORS.qaOverlap, t.mapPreview.qaOverlaps, n(qa.overlaps)],
                      [MAP_COLORS.qaSliver, t.mapPreview.qaSlivers, n(qa.slivers ?? 0)],
                    ]}
                  />
                )}
              </LayerOption>
            )}
          </Section>
        </TabsContent>
      </Tabs>
    </aside>
  );
}

function Section({
  icon,
  title,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="reveal">
      <div className="mb-2 flex items-center justify-between border-b border-dashed border-line pb-1.5">
        <h3 className="flex items-center gap-1.5 font-display text-[12px] font-bold uppercase tracking-[0.14em] text-ink-muted">
          {icon} {title}
        </h3>
        {action}
      </div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

/** One layer as a card: colour dot, name, count, on/off switch. */
function LayerOption({
  color,
  label,
  count,
  on,
  onToggle,
  extra,
  hint,
  children,
}: {
  color: string;
  label: string;
  count?: string;
  on: boolean;
  onToggle: () => void;
  extra?: ReactNode;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={clsx(
        "lift rounded-md border",
        on ? "border-l-4 bg-surface" : "border-line bg-surface-sunken/50"
      )}
      style={on ? { borderColor: `${color}66`, borderLeftColor: color } : undefined}
    >
      <div className="flex items-center gap-2 px-2.5 py-2">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left" title={hint ?? label}>
          <span
            className={clsx("h-2.5 w-2.5 flex-none rounded-full", !on && "opacity-40")}
            style={{ backgroundColor: color, boxShadow: "0 0 0 1px rgba(0,0,0,0.15)" }}
          />
          <span className={clsx("min-w-0 flex-1 truncate text-xs font-semibold", on ? "text-ink" : "text-ink-muted")}>
            {label}
          </span>
          {count !== undefined && <span className="tabular flex-none text-2xs text-ink-muted">{count}</span>}
        </button>
        {extra}
        <Switch checked={on} onChange={onToggle} label={label} />
      </div>
      {children && <div className="border-t border-line px-2.5 py-1.5">{children}</div>}
    </div>
  );
}

function Key({ rows }: { rows: [string, string, string][] }) {
  return (
    <ul className="space-y-0.5">
      {rows.map(([color, label, count]) => (
        <li key={label} className="flex items-center gap-2">
          <span className="h-2 w-2 flex-none rounded-sm" style={{ backgroundColor: color }} />
          <span className="text-2xs text-ink-secondary">{label}</span>
          <span className="tabular ml-auto text-2xs text-ink-muted">{count}</span>
        </li>
      ))}
    </ul>
  );
}

/** Zone / Ward / Block, stacked: each list narrows to the level above it. */
function LocationFilter({
  hierarchy,
  filter,
  onChange,
}: {
  hierarchy: Hierarchy;
  filter: HierarchyFilter;
  onChange: (next: HierarchyFilter) => void;
}) {
  const { t } = useLanguage();
  const w = t.workspace;
  const fb = t.hierarchyFilterBar;
  const zones = byName(hierarchy.zones);
  const wards = byName(wardsFor(hierarchy, filter.zoneId));
  const blocks = filter.wardId === null ? [] : byName(blocksFor(hierarchy, filter.zoneId, filter.wardId));
  const set = (next: Partial<HierarchyFilter>) => {
    const merged = { ...filter, ...next };
    if (merged.wardId === null) merged.blockId = null;
    onChange(reconcile(hierarchy, merged));
  };

  if (hierarchy.zones.length === 0 && hierarchy.wards.length === 0) {
    return <p className="text-xs text-ink-muted">{w.noHierarchy}</p>;
  }

  const field = (label: string, control: ReactNode) => (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-2xs font-bold uppercase tracking-[0.12em] text-ink-muted">
        <MapPin className="h-3 w-3" /> {label}
      </span>
      {control}
    </label>
  );
  const trigger = "h-10 w-full border-line bg-surface text-xs font-semibold shadow-sm";

  return (
    <div className="space-y-4">
      {field(
        w.zone,
        <Select value={toValue(filter.zoneId)} onValueChange={(v) => set({ zoneId: fromValue(v) })}>
          <SelectTrigger className={trigger}>
            <SelectValue placeholder={fb.allZones} />
          </SelectTrigger>
          <SelectContent {...DROP_DOWN}>
            <SelectItem value={ALL}>{fb.allZones}</SelectItem>
            {zones.map((z) => (
              <SelectItem key={z.id} value={String(z.id)}>{z.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {field(
        w.ward,
        <Select value={toValue(filter.wardId)} onValueChange={(v) => set({ wardId: fromValue(v) })}>
          <SelectTrigger className={trigger} disabled={wards.length === 0}>
            <SelectValue placeholder={fb.allWardsPlaceholder} />
          </SelectTrigger>
          <SelectContent {...DROP_DOWN}>
            <SelectItem value={ALL}>{fb.allWards(wards.length)}</SelectItem>
            {wards.map((x) => (
              <SelectItem key={x.id} value={String(x.id)}>{x.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {field(
        w.block,
        <Select
          value={filter.wardId === null ? "" : toValue(filter.blockId)}
          onValueChange={(v) => set({ blockId: fromValue(v) })}
        >
          <SelectTrigger className={trigger} disabled={blocks.length === 0}>
            <SelectValue placeholder={filter.wardId === null ? fb.selectWardFirst : fb.allBlocksPlaceholder} />
          </SelectTrigger>
          <SelectContent {...DROP_DOWN}>
            <SelectItem value={ALL}>{fb.allBlocks(blocks.length)}</SelectItem>
            {blocks.map((b) => (
              <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
